import { useState } from "react";
import { Button, Paper, Stack, Typography } from "@mui/material";
import ChargesSummaryCard from "components/ModulePayments/ChargesSummaryCard";
import ModulePaymentsTab from "components/ModulePayments/ModulePaymentsTab";
import { useModulePayments } from "components/ModulePayments/modulePaymentsApi";
import { PRIMARY_CHARGE_LABELS } from "components/ModulePayments/modulePaymentsConstants";
import {
  balanceDue,
  chargeSummary,
  totalVerifiedPaid,
} from "components/ModulePayments/modulePaymentsLogic";
import SendNotificationModal from "../../Accounts/SendNotificationModal";

const notifyMessage = (data, totalPaid, due) =>
  [
    `RPL payment update for ${data?.name || "Client"}.`,
    data?.certificate ? `Certificate: ${data.certificate}.` : null,
    totalPaid > 0 ? `Total paid: $${totalPaid.toFixed(2)}.` : null,
    due > 0 ? `Balance due: $${due.toFixed(2)}.` : "Fully paid.",
  ]
    .filter(Boolean)
    .join(" ");

const UnsavedPayments = ({ breakdown }) => (
  <Stack spacing={3}>
    <ChargesSummaryCard
      charges={breakdown}
      primaryLabel={PRIMARY_CHARGE_LABELS.RPL_CLIENT}
      totalPaid={0}
      balanceDue={balanceDue(breakdown.finalAmount, 0)}
    />
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" className="text-center">
        Save the RPL certificate first to start tracking payments.
      </Typography>
    </Paper>
  </Stack>
);

const PaymentsTab = ({ data, isLoading, navigate, usersList = [] }) => {
  const [notifyOpen, setNotifyOpen] = useState(false);
  const { data: payments = [] } = useModulePayments("RPL_CLIENT", data?.id);
  const charges = {
    primaryCharge: data?.cost,
    serviceCharge: data?.serviceCharge,
    extraCharges: data?.extraCharges,
    primaryChargeGst: data?.costGst,
    serviceChargeGst: data?.serviceChargeGst,
    extraChargesGst: data?.extraChargesGst,
    discount: data?.discount,
  };
  const breakdown = chargeSummary(charges);
  const totalPaid = totalVerifiedPaid(payments);
  const due = balanceDue(breakdown.finalAmount, totalPaid);

  return (
    <div className="space-y-4">
      <SendNotificationModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        users={usersList}
        context={{
          clientId: data?.clientId,
          caseOfficer: data?.caseOfficer,
          module: "rpl",
          defaultMessage: notifyMessage(data, totalPaid, due),
        }}
      />
      {data?.id ? (
        <ModulePaymentsTab
          moduleType="RPL_CLIENT"
          moduleId={data.id}
          clientId={data.clientId}
          charges={charges}
          onPaymentSubmitted={() => setNotifyOpen(true)}
        />
      ) : (
        <UnsavedPayments breakdown={breakdown} />
      )}
      <div className="px-6 py-4 border-t border-app-border flex justify-end gap-4">
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
        <Button variant="contained" type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : data?.id ? "Update" : "Submit"}
        </Button>
      </div>
    </div>
  );
};

export default PaymentsTab;
